import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value, onChange, type = "text", testid, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <label className="hairline opacity-50 block mb-2">{label}</label>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
      className="invisible-input"
      data-testid={testid}
    />
  </div>
);

const Area = ({ label, value, onChange, testid, rows = 3 }) => (
  <div>
    <label className="hairline opacity-50 block mb-2">{label}</label>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full bg-transparent border border-[color:var(--rule)] p-3 font-mono text-sm focus:outline-none focus:border-[color:var(--ink)]"
      data-testid={testid}
    />
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-serif italic text-2xl md:text-3xl mt-14 mb-6 border-t border-[color:var(--rule)] pt-8">
    {children}
  </h3>
);

export const The190Admin = ({ token }) => {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/the190`)
      .then((r) => setDoc(r.data))
      .catch(() => toast("Could not load Collector Editions content."));
  }, []);

  if (!doc) return null;

  const set = (key, value) => setDoc((d) => ({ ...d, [key]: value }));
  const setEdition = (key, field, value) =>
    setDoc((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  const setList = (key, i, field, value) =>
    setDoc((d) => {
      const list = [...(d[key] || [])];
      list[i] = { ...list[i], [field]: value };
      return { ...d, [key]: list };
    });
  const removeAt = (key, i) =>
    setDoc((d) => ({ ...d, [key]: d[key].filter((_, j) => j !== i) }));
  const addTo = (key, item) => setDoc((d) => ({ ...d, [key]: [...(d[key] || []), item] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        edition_one: doc.edition_one,
        edition_two: doc.edition_two,
        price_single: doc.price_single,
        price_pair: doc.price_pair,
        pair_link: doc.pair_link,
        delivery_info: doc.delivery_info,
        quotes: doc.quotes,
      };
      const res = await axios.put(`${API}/the190`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDoc(res.data);
      toast("Collector Editions updated. The live page reflects it immediately.");
    } catch (e) {
      toast(e?.response?.status === 401 ? "Session expired." : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-24 md:mt-32" data-testid="admin-the190">
      <div className="rule opacity-30 mb-14" />
      <div className="flex flex-wrap items-baseline justify-between gap-6">
        <div>
          <h2 className="font-serif italic text-4xl md:text-5xl">Collector Editions.</h2>
          <p className="font-serif italic text-lg md:text-xl opacity-70 mt-3">
            Edition artwork, stories, prices, direct product links and independent
            recognition. Changes appear on{" "}
            <span className="font-mono text-sm">/collector-editions</span> instantly.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="hairline bg-[color:var(--ink)] text-[color:var(--bone)] px-8 py-4 hover:bg-[color:var(--oxblood)] transition-colors duration-500 disabled:opacity-40"
          data-testid="the190-admin-save"
        >
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>

      <SectionTitle>Pricing &amp; pair link</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-8 max-w-4xl">
        <Field
          label="Single edition price"
          value={doc.price_single}
          onChange={(v) => set("price_single", v)}
          testid="the190-admin-price-single"
        />
        <Field
          label="Collector Pair price"
          value={doc.price_pair}
          onChange={(v) => set("price_pair", v)}
          testid="the190-admin-price-pair"
        />
        <Field
          label="Collector Pair product URL"
          value={doc.pair_link}
          onChange={(v) => set("pair_link", v)}
          testid="the190-admin-pair-link"
        />
      </div>

      {["edition_one", "edition_two"].map((key, idx) => (
        <div key={key}>
          <SectionTitle>Edition {idx === 0 ? "One" : "Two"}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            <Field
              label="Artwork title"
              value={doc[key]?.title}
              onChange={(v) => setEdition(key, "title", v)}
              testid={`the190-admin-${key}-title`}
            />
            <Field
              label="Image path"
              value={doc[key]?.image}
              onChange={(v) => setEdition(key, "image", v)}
              testid={`the190-admin-${key}-image`}
            />
            <div className="md:col-span-2">
              <Field
                label="Direct product page URL (Wix)"
                value={doc[key]?.link}
                onChange={(v) => setEdition(key, "link", v)}
                testid={`the190-admin-${key}-link`}
              />
            </div>
            <div className="md:col-span-2">
              <Area
                label="About the work (editorial description)"
                value={doc[key]?.story}
                onChange={(v) => setEdition(key, "story", v)}
                rows={5}
                testid={`the190-admin-${key}-story`}
              />
            </div>
          </div>
        </div>
      ))}

      <SectionTitle>Delivery information (FAQ)</SectionTitle>
      <div className="max-w-4xl">
        <Area
          label="How are editions delivered?"
          value={doc.delivery_info}
          onChange={(v) => set("delivery_info", v)}
          testid="the190-admin-delivery"
        />
      </div>

      <SectionTitle>Recognition quotes</SectionTitle>
      <p className="hairline opacity-45 mb-8 normal-case !tracking-widest max-w-2xl leading-relaxed">
        Use exact approved quotations only.
      </p>
      <div className="space-y-10 max-w-4xl">
        {(doc.quotes || []).map((q, i) => (
          <div key={q.id || i} className="grid grid-cols-1 md:grid-cols-2 gap-6 border-l-2 border-[color:var(--rule)] pl-6">
            <Field label="Name" value={q.name} onChange={(v) => setList("quotes", i, "name", v)} testid={`the190-admin-quote-${i}-name`} />
            <Field label="Role" value={q.role} onChange={(v) => setList("quotes", i, "role", v)} testid={`the190-admin-quote-${i}-role`} />
            <div className="md:col-span-2">
              <Area label="Approved quote" value={q.body} onChange={(v) => setList("quotes", i, "body", v)} rows={2} testid={`the190-admin-quote-${i}-body`} />
            </div>
            <button type="button" onClick={() => removeAt("quotes", i)} className="hairline opacity-40 hover:opacity-90 text-left" data-testid={`the190-admin-quote-${i}-remove`}>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addTo("quotes", { id: `q-${Date.now()}`, name: "", role: "", body: "", verified: false })}
          className="hairline border border-[color:var(--ink)] px-5 py-3"
          data-testid="the190-admin-quote-add"
        >
          + Add quote card
        </button>
      </div>

      <div className="mt-16">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="hairline bg-[color:var(--ink)] text-[color:var(--bone)] px-8 py-4 hover:bg-[color:var(--oxblood)] transition-colors duration-500 disabled:opacity-40"
          data-testid="the190-admin-save-bottom"
        >
          {saving ? "Saving…" : "Save all changes"}
        </button>
      </div>
    </div>
  );
};

export default The190Admin;
